<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div class="fixed inset-0 transition-opacity" @click="close">
        <div class="absolute inset-0 bg-black opacity-50"></div>
      </div>

      <!-- Modal panel -->
      <div class="inline-block align-bottom bg-background-light rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
        <!-- Modal Header -->
        <div class="bg-background-light px-6 pt-5 pb-3 border-b border-background-lighter">
          <div class="flex items-start justify-between mb-3">
            <div class="flex-1">
              <h3 class="text-xl font-semibold text-text-primary mb-3">Order Details</h3>
              <div class="flex items-center gap-2 flex-wrap">
                <span class="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold bg-primary text-white">
                  #ESK-{{ order.id }}
                </span>
                <span :class="getStatusBadgeClass(order.status)" class="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold">
                  <svg class="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                    <path v-if="order.status === 1 || order.status === 'completed'" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/>
                    <path v-else d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"/>
                  </svg>
                  {{ getStatusText(order.status) }}
                </span>
              </div>
            </div>
            <button
                @click="close"
                class="text-text-secondary hover:text-text-primary transition-colors ml-4"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <p class="text-sm text-text-secondary flex items-center">
            <svg class="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
            </svg>
            Ordered on {{ formatDate(order.created_at) }}
          </p>
        </div>

        <!-- Modal Body -->
        <div class="bg-background-light px-6 py-4">
          <!-- Order Summary Card -->
          <div class="bg-background rounded-lg border border-background-lighter p-4 mb-4">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <h4 class="text-base font-semibold text-text-primary mb-2">
                  {{ order.article_libelle || order.article_title || 'N/A' }}
                </h4>
                <div class="flex items-center gap-4 text-sm text-text-secondary">
                  <span class="flex items-center">
                    <svg class="w-4 h-4 mr-1.5 text-primary" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/>
                    </svg>
                    Quantity: {{ order.quantity || 1 }}
                  </span>
                  <span v-if="!shouldHidePrices" class="flex items-center">
                    <svg class="w-4 h-4 mr-1.5 text-primary" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clip-rule="evenodd"/>
                    </svg>
                    Price:
                    <span v-if="order.coupon_discount > 0" class="line-through mx-1">
                      {{ formatCurrency(order.coupon_discount + (order.amount), order.is_da) }}
                    </span>
                    <span class="font-semibold">{{ formatCurrency(order.amount, order.is_da) }}</span>
                  </span>
                </div>
              </div>
              <div class="flex items-center justify-center">
                <svg v-if="order.status === 1 || order.status === 'completed'" class="w-12 h-12 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <svg v-else-if="order.status === -1 || order.status === 'cancelled'" class="w-12 h-12 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                </svg>
                <svg v-else class="w-12 h-12 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                </svg>
              </div>
            </div>
          </div>

          <!-- Player Information -->
          <div v-if="order.playerinfo || order.player_info" class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
            <div class="flex items-start">
              <svg class="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
              </svg>
              <div>
                <h6 class="font-semibold text-blue-900 dark:text-blue-100 mb-1">Player Information</h6>
                <p class="text-sm text-blue-800 dark:text-blue-200">{{ order.playerinfo || order.player_info }}</p>
              </div>
            </div>
          </div>

          <!-- Response/Details Section -->
          <div v-if="order.details" class="bg-background rounded-lg border border-background-lighter overflow-hidden">
            <div class="bg-background-light px-4 py-3 border-b border-background-lighter flex items-center justify-between">
              <h6 class="font-semibold text-text-primary flex items-center">
                <svg class="w-5 h-5 text-primary mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd"/>
                </svg>
                Response
              </h6>
              <button
                  @click="copyToClipboard(order.details)"
                  class="inline-flex items-center px-3 py-1.5 text-sm border border-primary text-primary hover:bg-primary hover:text-white rounded-md transition-colors"
              >
                <svg class="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"/>
                  <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"/>
                </svg>
                Copy All
              </button>
            </div>
            <div class="p-4 space-y-2">
              <div
                  v-for="(line, index) in getDetailLines(order.details)"
                  :key="index"
                  class="group flex items-center justify-between bg-background-lighter rounded-md p-3 hover:bg-background-light transition-colors"
              >
                <code class="font-mono text-sm text-text-primary flex-1 break-words">
                  {{ line }}
                </code>
                <button
                    @click="copyToClipboard(line)"
                    class="ml-3 flex-shrink-0 inline-flex items-center px-2 py-1 text-xs border border-primary/30 text-primary hover:bg-primary hover:text-white rounded transition-colors opacity-0 group-hover:opacity-100"
                    :title="`Copy: ${line}`"
                >
                  <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"/>
                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- No Details - Check Status -->
          <div v-else>
            <!-- Finished Status - Show Player Info -->
            <div v-if="order.status === 1 || order.status === '1'" class="text-center py-8">
              <svg class="w-16 h-16 text-green-500 mx-auto mb-3" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              <h6 class="text-lg font-semibold text-text-primary mb-2">Order Completed</h6>

              <div v-if="order.playerinfo?.trim() || order.player_info?.trim()" class="mt-4 bg-background-light rounded-lg p-4 max-w-md mx-auto">
                <p class="text-sm text-text-secondary mb-2">Player Information:</p>
                <p class="text-text-primary font-medium">{{ (order.playerinfo || order.player_info).trim() }}</p>
              </div>

              <p v-else class="text-sm text-text-secondary mt-2">
                Your order has been completed successfully
              </p>
            </div>

            <!-- Processing State -->
            <div v-else class="text-center py-8">
              <svg class="w-16 h-16 text-text-secondary mx-auto mb-3 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
              </svg>
              <h6 class="text-lg font-semibold text-text-primary mb-1">Processing your order...</h6>
              <p class="text-sm text-text-secondary">You'll receive your items shortly</p>

              <div v-if="order.playerinfo?.trim() || order.player_info?.trim()" class="mt-4 bg-background-light rounded-lg p-4 max-w-md mx-auto">
                <p class="text-sm text-text-secondary mb-2">Player Information:</p>
                <p class="text-text-primary font-medium">{{ (order.playerinfo || order.player_info).trim() }}</p>
              </div>
            </div>
          </div>


        </div>

        <!-- Modal Footer -->
        <div class="bg-background-lighter px-6 py-4 border-t border-background-lighter">
          <div class="flex items-center justify-between">
            <p class="text-sm text-text-secondary">
              Order #ESK-{{ order.id }} • {{ getRelativeTime(order.created_at) }}
            </p>
            <button
                @click="close"
                class="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors font-medium"
            >
              <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
              </svg>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import { defineProps, defineEmits, computed } from 'vue';
import { useDataStore } from '../stores/dataStore';
import { useAuthStore } from '../stores/auth';
import Swal from 'sweetalert2';

export default {
  name: 'OrderDetailsModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    order: {
      type: Object,
      required: true
    }
  },
  setup(props, { emit }) {
    const dataStore = useDataStore();
    const authStore = useAuthStore();

    const shouldHidePrices = computed(() => authStore.shouldHidePrices);

    const close = () => {
      emit('close');
    };

    const formatCurrency = (amount, is_da = null) => {
      if (!amount && amount !== 0) return '0.00';

      // Get currency symbol based on is_da parameter
      let symbol = '$'; // Default

      if (is_da !== null && is_da !== undefined) {
        switch (parseInt(is_da)) {
          case 2:
            symbol = 'Dzd';
            break;
          case 1:
            symbol = '€';
            break;
          case 3:
            symbol = 'EGP';
            break;
          default:
            symbol = '$';
            break;
        }
      } else {
        // Fallback to dataStore if is_da not provided
        symbol = dataStore?.currencySymbol || '$';
      }
      const convertedAmount = (typeof amount === 'number' ? amount.toFixed(2) : parseFloat(amount || 0).toFixed(2));


      // Format display based on currency type
      switch (parseInt(is_da)) {
        case 2: // Dzd - Display after amount
          return `${convertedAmount} ${symbol}`;
        case 1: // Euro - Display before amount
          return `${symbol}${convertedAmount}`;
        case 3: // EGP - Display after amount
          return `${convertedAmount} ${symbol}`;
        default: // Dollar - Display before amount
          return `${symbol}${convertedAmount}`;
      }
    };


    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const getRelativeTime = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      return formatDate(dateString);
    };

    const getStatusText = (status) => {
      const statusNum = typeof status === 'string' ? parseInt(status) : status;
      const texts = {
        0: 'Pending',
        1: 'Completed',
        4: 'Processing',
        '-3': 'Cancelling',
        '-1': 'Cancelled'
      };
      return texts[statusNum] || 'Unknown';
    };

    const getStatusBadgeClass = (status) => {
      const statusNum = typeof status === 'string' ? parseInt(status) : status;
      const classes = {
        0: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
        1: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        4: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
        '-3': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
        '-1': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
      };
      return classes[statusNum] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    };

    const getDetailLines = (details) => {
      if (!details) return [];

      // Split by ANY whitespace (spaces, tabs, newlines)
      return details
          .split(/\s+/)  // Split by one or more whitespace characters
          .map(line => line.trim())
          .filter(line => line.length > 0);
    };

// Enhanced copy to clipboard with feedback
    const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text);

        // Show success toast (you can customize this)
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Copied to clipboard!',
          showConfirmButton: false,
          timer: 1500,
          timerProgressBar: true
        });
      } catch (err) {
        console.error('Failed to copy:', err);

        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();

        try {
          document.execCommand('copy');
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: 'Copied to clipboard!',
            showConfirmButton: false,
            timer: 1500
          });
        } catch (err2) {
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'error',
            title: 'Failed to copy',
            showConfirmButton: false,
            timer: 1500
          });
        }

        document.body.removeChild(textArea);
      }
    };

    return {
      close,
      formatCurrency,
      formatDate,
      getRelativeTime,
      getStatusText,
      getStatusBadgeClass,
      getDetailLines,
      copyToClipboard,
      shouldHidePrices
    };
  }
}
</script>
